import { useCallback, useState } from 'react';
import { Shape } from '../types/Shape';
import { useToast } from '../contexts/ToastContext';
import * as pdfjsLib from 'pdfjs-dist';

export interface UseCanvasDropHandlersProps {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;
  stagePos: { x: number; y: number };
  stageScale: number;
  canvasContainerRef: React.RefObject<HTMLDivElement | null>;
}

export interface UseCanvasDropHandlersReturn {
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  isDragOver: boolean;
}

export function useCanvasDropHandlers({
  shapes,
  setShapes,
  setSelectedShapes,
  setShowResizeHandles,
  stagePos,
  stageScale,
  canvasContainerRef,
}: UseCanvasDropHandlersProps): UseCanvasDropHandlersReturn {
  const toast = useToast();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // relatedTarget이 캔버스 밖이면 드래그 오버 해제
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const getNextOrder = useCallback(() => {
    const maxOrder = Math.max(...shapes.map(s => s.order ?? 0), 0);
    return maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;
  }, [shapes]);

  const getDropPosition = useCallback((e: React.DragEvent, offsetIdx = 0) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: (e.clientX - rect.left - stagePos.x) / stageScale + offsetIdx * 30,
      y: (e.clientY - rect.top - stagePos.y) / stageScale + offsetIdx * 30,
    };
  }, [canvasContainerRef, stagePos, stageScale]);

  const processImageFile = useCallback((file: File, e: React.DragEvent, offsetIdx: number) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const dataUrl = readerEvent.target?.result as string;
      if (!dataUrl) return;
      const img = new window.Image();
      img.onload = () => {
        const pos = getDropPosition(e, offsetIdx);
        if (!pos) return;
        const imgWidth = Math.min(img.width, 800);
        const imgHeight = (img.height / img.width) * imgWidth;
        const newShape: Shape = {
          id: `${Date.now()}-${offsetIdx}`,
          type: 'image',
          x: pos.x,
          y: pos.y,
          width: imgWidth,
          height: imgHeight,
          imageDataUrl: dataUrl,
          stroke: '#D1D5DB',
          strokeWidth: 1,
          order: getNextOrder() + offsetIdx * 2,
          renderMode: 'html',
        };
        setShapes(prev => [...prev, newShape]);
        setSelectedShapes([newShape.id]);
        setShowResizeHandles(true);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }, [getDropPosition, getNextOrder, setShapes, setSelectedShapes, setShowResizeHandles]);

  const processAudioFile = useCallback((file: File, e: React.DragEvent, offsetIdx: number) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const audioUrl = readerEvent.target?.result as string;
      const pos = getDropPosition(e, offsetIdx);
      if (!pos) return;
      const newShape: Shape = {
        id: `${Date.now()}-${offsetIdx}`,
        type: 'audio',
        x: pos.x,
        y: pos.y,
        width: 300,
        height: 60,
        audioUrl,
        renderMode: 'html',
        order: getNextOrder() + offsetIdx * 2,
        stroke: '#d1d5db',
        strokeWidth: 1,
      };
      setShapes(prev => [...prev, newShape]);
      setSelectedShapes([newShape.id]);
      setShowResizeHandles(true);
    };
    reader.readAsDataURL(file);
  }, [getDropPosition, getNextOrder, setShapes, setSelectedShapes, setShowResizeHandles]);

  const processPdfFile = useCallback((file: File, e: React.DragEvent, offsetIdx: number) => {
    const reader = new FileReader();
    reader.onload = async (readerEvent) => {
      const dataUrl = readerEvent.target?.result;
      if (typeof dataUrl !== 'string') return;
      const pos = getDropPosition(e, offsetIdx);
      if (!pos) return;
      try {
        const pdf = await pdfjsLib.getDocument({
          url: dataUrl,
          useWorkerFetch: false,
          disableAutoFetch: false,
        }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.0 });
        let initialWidth = viewport.width;
        let initialHeight = viewport.height;
        if (initialWidth > 800) {
          const ratio = 800 / initialWidth;
          initialWidth = 800;
          initialHeight = initialHeight * ratio;
        }
        const newShape: Shape = {
          id: `${Date.now()}-${offsetIdx}`,
          type: 'pdf',
          x: pos.x,
          y: pos.y,
          width: initialWidth,
          height: initialHeight,
          pdfFile: file,
          pdfUrl: URL.createObjectURL(file),
          _pdfBase64: dataUrl,
          currentPage: 1,
          totalPages: pdf.numPages,
          renderMode: 'html',
          order: getNextOrder() + offsetIdx * 2,
          stroke: '#d1d5db',
          strokeWidth: 1,
        };
        setShapes(prev => [...prev, newShape]);
        setSelectedShapes([newShape.id]);
        setShowResizeHandles(true);
      } catch {
        toast.error('PDF 파일을 불러올 수 없습니다.');
      }
    };
    reader.readAsDataURL(file);
  }, [getDropPosition, getNextOrder, setShapes, setSelectedShapes, setShowResizeHandles, toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    let processedCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        processImageFile(file, e, i);
        processedCount++;
      } else if (file.type === 'application/pdf') {
        processPdfFile(file, e, i);
        processedCount++;
      } else if (file.type.startsWith('audio/')) {
        processAudioFile(file, e, i);
        processedCount++;
      }
    }

    if (processedCount === 0) {
      toast.warning('지원하지 않는 파일 형식입니다. (이미지, PDF, 오디오 지원)');
    }
  }, [processImageFile, processPdfFile, processAudioFile, toast]);

  return {
    handleDragOver,
    handleDragLeave,
    handleDrop,
    isDragOver,
  };
}
